<template>
  <div class="terminal-select">
    <p v-if="!cityId" class="terminal-select__hint">Сначала выберите город доставки</p>
    <p v-else-if="loading" class="terminal-select__hint">Загружаем терминалы…</p>
    <div v-else-if="loadError" class="terminal-select__unavailable">
      <p class="terminal-select__hint">Доставка до терминала временно недоступна</p>
      <UiButton variant="secondary" @click="loadTerminals">Повторить</UiButton>
    </div>
    <p v-else-if="terminals.length === 0" class="terminal-select__hint">
      В выбранном городе нет терминалов — выберите доставку до адреса
    </p>
    <div v-else class="terminal-select__list">
      <label v-for="terminal in terminals" :key="terminal.id" class="terminal-select__option">
        <input
          type="radio"
          name="delivery-terminal"
          :value="terminal.id"
          :checked="modelValue === terminal.id"
          @change="pick(terminal)"
        />
        <span class="terminal-select__option-body">
          <strong>{{ terminal.name }}</strong>
          <span v-if="terminal.address" class="terminal-select__address">{{ terminal.address }}</span>
        </span>
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import UiButton from '@/shared/ui/UiButton.vue'
import { deliveryApi, type CarrierTerminalOption } from '@/modules/order/api/delivery.api'

const props = defineProps<{
  cityId: number | null
  modelValue: string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void
  (e: 'select', terminal: CarrierTerminalOption | null): void
}>()

const terminals = ref<CarrierTerminalOption[]>([])
const loading = ref(false)
const loadError = ref(false)
let load_request_id = 0

function pick(terminal: CarrierTerminalOption): void {
  emit('update:modelValue', terminal.id)
  emit('select', terminal)
}

function clearSelection(): void {
  // Пустой выбор не «сбрасывается»: иначе монтирование с null слало бы лишние события
  if (props.modelValue === null) return
  emit('update:modelValue', null)
  emit('select', null)
}

async function loadTerminals(): Promise<void> {
  // Ответ для прошлого города не должен перезаписать список текущего
  const request_id = ++load_request_id

  if (props.cityId === null) {
    terminals.value = []
    loadError.value = false
    return
  }

  loading.value = true
  loadError.value = false
  try {
    const list = await deliveryApi.getTerminals(props.cityId)
    if (request_id !== load_request_id) return
    terminals.value = list
    // Город мог смениться, пока шёл запрос: выбор из прошлого списка неактуален
    if (props.modelValue !== null && !list.some((t) => t.id === props.modelValue)) {
      clearSelection()
    }
  } catch (e) {
    if (request_id !== load_request_id) return
    console.error('Ошибка загрузки терминалов доставки', e)
    terminals.value = []
    loadError.value = true
    clearSelection()
  } finally {
    if (request_id === load_request_id) loading.value = false
  }
}

// immediate: компонент появляется при выборе способа «до терминала», смена радио
// не меняет cityId — без immediate список не загрузился бы при первом показе.
// Выбор сбрасывается только при реальной смене города: на монтировании modelValue
// может быть восстановлен из черновика или сохранён при переключении способа доставки.
watch(
  () => props.cityId,
  (_city_id, previous_city_id) => {
    if (previous_city_id !== undefined) {
      clearSelection()
    }
    loadTerminals()
  },
  { immediate: true },
)
</script>

<style scoped>
.terminal-select__hint {
  margin: 0;
  color: #586578;
  font-size: 14px;
}

.terminal-select__unavailable {
  display: flex;
  align-items: center;
  gap: 12px;
}

.terminal-select__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.terminal-select__option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #dfe4ea;
  border-radius: 8px;
  cursor: pointer;
}

.terminal-select__option:has(input:checked) {
  border-color: #2563eb;
  background: #f0f6ff;
}

.terminal-select__option input {
  margin-top: 3px;
}

.terminal-select__option-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.terminal-select__address {
  color: #586578;
  font-size: 13px;
}
</style>
